import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Divider,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import MenuIcon from "@material-ui/icons/Menu";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import DashboardRoundedIcon from "@material-ui/icons/DashboardRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltRoundedIcon from "@material-ui/icons/SyncAltRounded";
import ContactsRoundedIcon from "@material-ui/icons/ContactsRounded";
import FlashOnRoundedIcon from "@material-ui/icons/FlashOnRounded";
import LocalOfferRoundedIcon from "@material-ui/icons/LocalOfferRounded";
import GroupWorkRoundedIcon from "@material-ui/icons/GroupWorkRounded";
import PeopleRoundedIcon from "@material-ui/icons/PeopleRounded";
import AccountTreeRoundedIcon from "@material-ui/icons/AccountTreeRounded";
import MemoryRoundedIcon from "@material-ui/icons/MemoryRounded";
import SettingsRoundedIcon from "@material-ui/icons/SettingsRounded";
import EventRoundedIcon from "@material-ui/icons/EventRounded";
import AllInclusiveRoundedIcon from "@material-ui/icons/AllInclusiveRounded";
import { useHistory, useLocation } from "react-router-dom";

import MainListItems from "./MainListItems";
import LupaRH from "../components/LupaRH";
import NotificationsPopOver from "../components/NotificationsPopOver";
import AvisoEntrevista from "../components/AvisoEntrevista";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { getBackendUrl } from "../config";
import api from "../services/api";
import { rotuloPerfil, poderDeAdmin } from "../utils/perfil";
import Flutuantes from "../components/Flutuantes";
import CampanhaDoMes from "../components/CampanhaDoMes";
import MarDeFundo from "../components/MarDeFundo";
import { svgOnda } from "../utils/estiloNavy";
import openSocket from "../services/socket-io";

// Status de disponibilidade que o atendente define no menu do topo.
// A IA só transfere clientes automaticamente para quem está "disponivel".
const STATUS_OPCOES = [
  { valor: "disponivel", rotulo: "Disponível", cor: "#0D9488", dica: "Recebe clientes" },
  { valor: "almoco", rotulo: "Em almoço", cor: "#E8A93B", dica: "Não recebe clientes" },
  { valor: "ausente", rotulo: "Ausente", cor: "#ef4444", dica: "Não recebe clientes" },
];

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    // sem isto, uma página um pouco mais larga que a tela empurrava o layout
    // inteiro para o lado (o menu ficava cortado à esquerda)
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
    position: "relative",
    // Fundo claro com o "mar" (caustica, raios e bolhas) por tras das paginas.
    backgroundColor: theme.palette.type === "dark" ? "#0b1312" : "#f4f7f7",
    backgroundImage: theme.palette.type === "dark"
      ? "linear-gradient(180deg, #0d1a19 0%, #0b1312 60%)"
      : "linear-gradient(180deg, #ecf8f6 0%, #f4f7f7 50%, #f1f6f6 100%)",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    // Barra lateral colada na borda: branca no claro, verde-escura lisa no escuro
    backgroundColor: theme.palette.type === "dark" ? "#101a19" : "#ffffff",
    margin: 0,
    height: "100%",
    borderRadius: 0,
    border: "none",
    borderRight: `1px solid ${theme.palette.type === "dark" ? "rgba(148,163,184,0.12)" : "#e3eaea"}`,
    boxShadow: "none",
    color: theme.palette.text.primary,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    zIndex: 1,
    '@media (max-width: 600px)': {
      width: 280,
      position: 'fixed',
      zIndex: 1300,
      height: '100vh',
    },
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: 72,
    '@media (max-width: 600px)': {
      width: 0,
      overflow: 'hidden',
      margin: 0,
      border: 0,
    },
  },

  logoArea: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 12px 0 20px",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(148,163,184,0.12)" : "#eef2f2"}`,
    height: 64,
    minHeight: 64,
  },
  toggleBtn: {
    padding: 6,
    color: "#94a3b8",
    "&:hover": {
      backgroundColor: "rgba(148,163,184,0.12)",
      color: "#e2e8f0",
    },
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    // folga para o robo flutuar/girar sem a antena e as orelhas serem cortadas
    padding: "8px 4px",
    margin: "-8px -4px",
  },
  logoImage: {
    height: 36,
    width: 36,
    flexShrink: 0,
    overflow: "visible",
    objectFit: "contain",
    cursor: "pointer",
    transition: "transform .2s ease",
    "&:hover": { transform: "rotate(-8deg)" },
  },
  logoText: {
    fontWeight: 800,
    marginLeft: 10,
    lineHeight: 0.95,
    display: "flex",
    flexDirection: "column",
  },
  logoTextIa: {
    color: "#0d9488",
    fontSize: "1.15rem",
    letterSpacing: 1,
  },

  empresa: {
    textAlign: "center",
    fontSize: "0.62rem",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    padding: "10px 0 12px",
    borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(148,163,184,0.12)" : "#eef2f2"}`,
    "& b": { color: "#0d9488", fontWeight: 700 },
  },
  logoTextConsig: {
    color: theme.palette.text.primary,
    fontSize: "0.82rem",
    letterSpacing: 0.4,
    whiteSpace: "nowrap",
    marginTop: 3,
  },

  mare: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 190,
    pointerEvents: "none",
    zIndex: 0,
    backgroundImage: [
      svgOnda(theme.palette.type === "dark" ? "rgba(45,212,191,0.07)" : "rgba(20,184,166,0.07)"),
      svgOnda(theme.palette.type === "dark" ? "rgba(45,212,191,0.05)" : "rgba(20,184,166,0.06)"),
    ].join(","),
    backgroundSize: "600px 70px, 420px 56px",
    backgroundRepeat: "repeat-x, repeat-x",
    backgroundPosition: "0 bottom, 0 calc(100% + 6px)",
    animation: "iaMar 26s linear infinite, iaMare 7s ease-in-out infinite",
  },
  bolhaLateral: {
    position: "absolute",
    bottom: 20,
    borderRadius: "50%",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(94,234,212,0.35)" : "rgba(13,148,136,0.30)"}`,
    background: theme.palette.type === "dark" ? "rgba(45,212,191,0.08)" : "rgba(255,255,255,0.9)",
    animation: "iaBolha 9s ease-in infinite",
  },
  sidebarContent: {
    position: "relative",
    zIndex: 1,
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: 4,
    },
  },

  // Topo: cartao flutuante de vidro, alinhado com o menu lateral
  // (mesma margem, mesmo raio) em vez de uma faixa branca colada.
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    padding: "0 20px 0 20px",
    margin: 0,
    height: 64,
    minHeight: 64,
    flexShrink: 0,
    borderRadius: 0,
    position: "relative",
    zIndex: 10,
    // faixa branca colada no topo, com uma linha fina embaixo
    backgroundColor: theme.palette.type === "dark" ? "#101a19" : "#ffffff",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(148,163,184,0.12)" : "#e3eaea"}`,
    boxShadow: "none",
    // reflexo de agua correndo na linha de baixo
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: -1,
      height: 2,
      background: "linear-gradient(90deg, transparent 0%, rgba(20,184,166,.0) 30%, rgba(20,184,166,.55) 50%, rgba(20,184,166,0) 70%, transparent 100%)",
      backgroundSize: "40% 100%",
      backgroundRepeat: "no-repeat",
      animation: "iaLinhaMar 9s ease-in-out infinite",
      pointerEvents: "none",
    },
    '@media (max-width: 600px)': {
      margin: "8px 8px 4px 8px",
      padding: '0 8px',
      height: 56,
      minHeight: 56,
    },
  },
  tituloIcone: {
    width: 40,
    height: 40,
    borderRadius: 12,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0d9488",
    background: theme.palette.type === "dark" ? "rgba(45,212,191,0.12)" : "#e6f6f4",
    "& svg": { fontSize: 21 },
    '@media (max-width: 600px)': { display: "none" },
  },
  tituloTextos: { display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.15 },
  migalha: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    "& b": { color: "#0D9488" },
    '@media (max-width: 600px)': { display: "none" },
  },
  divisorTopo: {
    width: 1,
    height: 28,
    background: theme.palette.type === "dark" ? "rgba(148,163,184,0.18)" : "#e5e9f0",
    margin: "0 4px",
  },
  usuarioChip: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "4px 12px 4px 4px",
    borderRadius: 999,
    cursor: "pointer",
    transition: "background-color .15s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
    },
  },
  usuarioNome: {
    fontSize: "0.85rem",
    fontWeight: 800,
    color: theme.palette.text.primary,
    maxWidth: 150,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  usuarioPerfil: {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#0D9488",
  },
  usuarioTextos: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.2,
    '@media (max-width: 760px)': { display: "none" },
  },
  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  greeting: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#0f1a19",
  },
  dateChip: {
    backgroundColor: "#f0fdfa",
    color: "#0D9488",
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 28,
    border: "1px solid #ccfbf1",
  },
  topBarRight: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  darkModeBtn: {
    padding: 8,
    color: theme.palette.text.secondary,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: "#0D9488",
    },
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f9fafb",
    border: "1px solid #e4e7ec",
    borderRadius: 8,
    padding: "6px 12px",
    gap: 8,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#d0d5dd",
    },
  },
  searchText: {
    color: "#98a2b3",
    fontSize: "0.85rem",
  },
  iconBtn: {
    padding: 8,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  userAvatar: {
    width: 38,
    height: 38,
    background: "linear-gradient(135deg, #0d9488, #14b8a6)",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 700,
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 0 0 3px rgba(13,148,136,0.25)",
    },
  },

  content: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    // acima do fundo; minWidth 0 deixa o conteúdo encolher em vez de
    // alargar a página
    position: "relative",
    zIndex: 1,
    minWidth: 0,
  },
  // Seletor de status sempre visivel no topo (antes ficava escondido no
  // menu do avatar e o pessoal esquecia de trocar).
  statusPill: {
    // mesmo tamanho e formato do selo de perfil ao lado
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    marginRight: 8,
    borderRadius: 16,
    border: "1px solid transparent",
    fontWeight: 700,
    fontSize: "0.8rem",
    lineHeight: 1.4,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    transition: "filter .12s ease",
    "&:hover": { filter: "brightness(0.95)" },
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  statusDotPulse: {
    animation: "$pulsoStatus 1.8s ease-in-out infinite",
  },
  "@keyframes pulsoStatus": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.35 },
    "100%": { opacity: 1 },
  },
  statusMenuPaper: {
    borderRadius: 14,
    minWidth: 240,
    padding: 6,
    marginTop: 6,
  },
  statusOpcao: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 10,
    cursor: "pointer",
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  statusAviso: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "12px 24px 0",
    padding: "12px 16px",
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: "solid",
    fontSize: "0.92rem",
    color: theme.palette.text.primary,
  },
  statusAvisoBtn: {
    marginLeft: "auto",
    borderRadius: 10,
    padding: "8px 14px",
    fontWeight: 800,
    fontSize: "0.85rem",
    cursor: "pointer",
    whiteSpace: "nowrap",
    // verde claro "vidro" com fonte verde escuro
    color: theme.palette.type === "dark" ? "#ffffff" : "#134e4a",
    backgroundColor: "rgba(20,184,166,0.18)",
    border: "1px solid rgba(20,184,166,0.45)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.55), 0 2px 8px rgba(13,148,136,0.12)",
    transition: "background-color .15s ease",
    "&:hover": { backgroundColor: "rgba(20,184,166,0.28)" },
  },
  contentBody: {
    flex: 1,
    overflow: "hidden",
    padding: 0,
  },

  menuBtn: {
    padding: 8,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  pageTitle: {
    fontSize: "1.2rem",
    fontWeight: 800,
    color: theme.palette.text.primary,
    letterSpacing: "-0.2px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    '@media (max-width: 600px)': { fontSize: "1.1rem" },
  },
  roleBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 12px",
    borderRadius: 16,
    backgroundColor: "rgba(13,148,136,0.14)",
    color: theme.palette.type === "dark" ? "#14b8a6" : "#0D9488",
    fontWeight: 700,
    fontSize: "0.8rem",
    marginLeft: 10,
    whiteSpace: "nowrap",
  },
  userMenuPaper: {
    minWidth: 250,
    borderRadius: 14,
    marginTop: 10,
    padding: 6,
    boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
  },
  menuHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px 12px",
  },
  menuHeaderAvatar: {
    width: 42,
    height: 42,
    backgroundColor: "#0D9488",
    fontWeight: 700,
  },
  menuHeaderName: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  menuHeaderRole: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#14b8a6" : "#0D9488",
  },
  menuSectionLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    padding: "6px 12px 4px",
  },
  modeRow: {
    display: "flex",
    gap: 8,
    padding: "0 8px 6px",
  },
  modeBtn: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "8px 6px",
    borderRadius: 10,
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  modeBtnActive: {
    color: "#0D9488",
    borderColor: "#0D9488",
    backgroundColor: "rgba(13,148,136,0.12)",
    "&:hover": {
      backgroundColor: "rgba(13,148,136,0.18)",
    },
  },
  menuDivider: {
    margin: "6px 8px",
  },
  userMenuItem: {
    borderRadius: 10,
    margin: "2px 6px",
    padding: "9px 12px",
    fontSize: "0.9rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  userMenuIcon: {
    color: theme.palette.text.secondary,
  },
  logoutItem: {
    color: "#e5484d",
    "& $userMenuIcon": {
      color: "#e5484d",
    },
    "&:hover": {
      backgroundColor: "rgba(229,72,77,0.12)",
      color: "#e5484d",
    },
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const history = useHistory();
  const location = useLocation();

  const [status, setStatus] = useState("disponivel");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/status");
        if (data?.disponibilidade) setStatus(data.disponibilidade);
      } catch (e) {
        /* mantém o padrão "disponivel" */
      }
    })();
  }, []);

  const [statusAnchor, setStatusAnchor] = useState(null);
  const mostraStatus = !!user?.id && user.profile !== "admin";
  const statusAtual =
    STATUS_OPCOES.find((o) => o.valor === status) || STATUS_OPCOES[0];

  // O admin pode trocar o status de alguem pela tela de Usuarios: a tela
  // dessa pessoa acompanha na hora.
  useEffect(() => {
    if (!user?.id) return;
    const socket = openSocket();
    const aoAtualizarUsuario = (data) => {
      if (data?.action === "update" && data.user?.id === user.id && data.user.disponibilidade) {
        setStatus(data.user.disponibilidade);
      }
    };
    socket.on("user", aoAtualizarUsuario);

    // Reconectou (computador acordou, internet voltou): o servidor pode ter
    // marcado como Ausente enquanto estava fora -- busca o status de novo.
    const aoReconectar = async () => {
      try {
        const { data } = await api.get("/me/status");
        if (data?.disponibilidade) setStatus(data.disponibilidade);
      } catch (e) {
        /* mantém o que está na tela */
      }
    };
    socket.on("connect", aoReconectar);

    return () => {
      socket.off("user", aoAtualizarUsuario);
      socket.off("connect", aoReconectar);
    };
  }, [user?.id]);

  // 20 min sem mexer no sistema (mouse, teclado, clique, rolagem) com o status
  // "Disponivel" -> vira "Ausente" sozinho, para nao cair cliente para quem
  // saiu e esqueceu de trocar. A ultima atividade e compartilhada entre as
  // abas (localStorage): mexer em uma aba vale para todas.
  const [autoAusente, setAutoAusente] = useState(false);
  const statusRef = React.useRef(status);
  statusRef.current = status;

  // Neste sistema NAO existe "ausente por inatividade": quem esta logado
  // fica disponivel e recebe os candidatos normalmente, mesmo sem mexer.

  const handleChangeStatus = async (novo) => {
    if (novo === "disponivel") setAutoAusente(false);
    const anterior = status;
    setStatus(novo);
    try {
      await api.put("/me/status", { disponibilidade: novo, manual: true });
    } catch (e) {
      setStatus(anterior);
    }
  };

  const pageTitles = {
    "/": "Dashboard",
    "/atendimentos": "Atendimentos",
    "/agenda": "Agenda de Entrevistas",
    "/conexoes": "Conexões",
    "/contatos": "Contatos",
    "/respostas-rapidas": "Respostas Rápidas",
    "/etiquetas": "Etiquetas",
    "/filas": "Filas de Atendimento",
    "/setores": "Filas de Atendimento",
    "/usuarios": "Usuários",
    "/fluxos-visuais": "Fluxos Visuais",
    "/chatbot-ia": "Assistente de RH (IA)",
    "/meta": "Meta Ads",
    "/configuracoes": "Configurações",
  };

  const ICONES_PAGINA = {
    "/": DashboardRoundedIcon,
    "/atendimentos": WhatsAppIcon,
    "/agenda": EventRoundedIcon,
    "/conexoes": SyncAltRoundedIcon,
    "/contatos": ContactsRoundedIcon,
    "/respostas-rapidas": FlashOnRoundedIcon,
    "/etiquetas": LocalOfferRoundedIcon,
    "/filas": GroupWorkRoundedIcon,
    "/setores": GroupWorkRoundedIcon,
    "/usuarios": PeopleRoundedIcon,
    "/fluxos-visuais": AccountTreeRoundedIcon,
    "/chatbot-ia": MemoryRoundedIcon,
    "/meta": AllInclusiveRoundedIcon,
    "/configuracoes": SettingsRoundedIcon,
  };

  const getPageIcon = () => {
    const path = location.pathname;
    if (ICONES_PAGINA[path]) return ICONES_PAGINA[path];
    const match = Object.keys(ICONES_PAGINA).find(p => p !== "/" && path.startsWith(p));
    return match ? ICONES_PAGINA[match] : DashboardRoundedIcon;
  };

  const ehAdministracao = () =>
    ["/conexoes", "/fluxos-visuais", "/chatbot-ia", "/meta", "/configuracoes", "/usuarios", "/filas", "/setores"]
      .some(p => location.pathname.startsWith(p));

  const getPageTitle = () => {
    const path = location.pathname;
    if (pageTitles[path]) return pageTitles[path];
    const match = Object.keys(pageTitles).find(
      p => p !== "/" && path.startsWith(p)
    );
    return match ? pageTitles[match] : "";
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    } else {
      setDrawerOpen(false);
    }
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <MarDeFundo />
      <Drawer
        variant={drawerVariant}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {}
        <div className={classes.logoArea}>
          <div className={classes.logoContainer}>
            {/* Lupa do RH (o mesmo desenho do icone da aba) */}
            <LupaRH className={classes.logoImage} cor={darkMode ? "#2dd4bf" : "#0f766e"} />
            {drawerOpen && (
              <Typography variant="h6" className={classes.logoText} translate="no">
                <span className={classes.logoTextIa}>I.A</span>
                <span className={classes.logoTextConsig}>Recursos Humanos</span>
              </Typography>
            )}
          </div>
          {drawerOpen && (
            <IconButton className={classes.toggleBtn} onClick={() => setDrawerOpen(false)}>
              <ChevronLeftIcon />
            </IconButton>
          )}
        </div>

        {}
        <div className={classes.sidebarContent}>
          <MainListItems drawerClose={drawerClose} drawerOpen={drawerOpen} />
        </div>

        {/* mare: ondas bem leves no pe da barra lateral */}
        <div className={classes.mare} aria-hidden="true">
          {[18, 46, 72, 88].map((x, i) => (
            <span
              key={x}
              className={classes.bolhaLateral}
              style={{ left: `${x}%`, animationDelay: `${i * 2.3}s`, width: 4 + (i % 3) * 2, height: 4 + (i % 3) * 2 }}
            />
          ))}
        </div>
        <CampanhaDoMes recolhida={!drawerOpen} />
      </Drawer>

      {}
      <div className={classes.content}>
        {}
        <div className={classes.topBar}>
          <div className={classes.topBarLeft}>
            {!drawerOpen && (
              <IconButton
                className={classes.menuBtn}
                onClick={() => setDrawerOpen(true)}
              >
                <ChevronRightIcon />
              </IconButton>
            )}
            {(() => {
              const IconePagina = getPageIcon();
              return (
                <span className={classes.tituloIcone} key={location.pathname} style={{ animation: "dbIconePop .4s ease" }}>
                  <IconePagina />
                </span>
              );
            })()}
            <div className={classes.tituloTextos}>
              <span className={classes.migalha}>
                I.A Recursos Humanos › <b>{ehAdministracao() ? "Administração" : "Menu"}</b>
              </span>
              <Typography className={classes.pageTitle}>
                {getPageTitle()}
              </Typography>
            </div>
          </div>

          <div className={classes.topBarRight}>
            {mostraStatus && (
              <>
                <Tooltip title="Seu status — clique para trocar" arrow>
                  <div
                    className={classes.statusPill}
                    onClick={(e) => setStatusAnchor(e.currentTarget)}
                    style={{
                      borderColor: `${statusAtual.cor}40`,
                      color: statusAtual.cor,
                      backgroundColor: `${statusAtual.cor}24`,
                    }}
                  >
                    <span
                      className={clsx(classes.statusDot, classes.statusDotPulse)}
                      style={{ backgroundColor: statusAtual.cor }}
                    />
                    {statusAtual.rotulo}
                    <span style={{ fontSize: 8, opacity: 0.7, marginLeft: 2 }}>▼</span>
                  </div>
                </Tooltip>
                <Menu
                  anchorEl={statusAnchor}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  open={Boolean(statusAnchor)}
                  onClose={() => setStatusAnchor(null)}
                  PaperProps={{ className: classes.statusMenuPaper }}
                >
                  {STATUS_OPCOES.map((op) => (
                    <div
                      key={op.valor}
                      className={classes.statusOpcao}
                      onClick={() => {
                        handleChangeStatus(op.valor);
                        setStatusAnchor(null);
                      }}
                      style={status === op.valor ? { backgroundColor: `${op.cor}1F` } : undefined}
                    >
                      <span className={classes.statusDot} style={{ backgroundColor: op.cor }} />
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: status === op.valor ? 800 : 600, fontSize: 14 }}>
                          {op.rotulo}
                        </span>
                        <span style={{ fontSize: 12, opacity: 0.65 }}>{op.dica}</span>
                      </div>
                      {status === op.valor && (
                        <span style={{ marginLeft: "auto", color: op.cor, fontWeight: 800 }}>✓</span>
                      )}
                    </div>
                  ))}
                </Menu>
              </>
            )}
            {user.id && (
              <>
                <NotificationsPopOver />
                <AvisoEntrevista />
              </>
            )}
            <span className={classes.divisorTopo} />
            <div className={classes.usuarioChip} onClick={handleMenu}>
              <span style={{ position: "relative", display: "inline-flex" }}>
                <Avatar
                  className={classes.userAvatar}
                  src={user.profileImage ? `${getBackendUrl()}public/${user.profileImage}` : undefined}
                >
                  {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                </Avatar>
                {user.profile !== "admin" && (
                  <span
                    title={(STATUS_OPCOES.find(o => o.valor === status) || {}).rotulo}
                    style={{
                      position: "absolute",
                      right: 0,
                      bottom: 0,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      border: "2px solid #fff",
                      backgroundColor: (STATUS_OPCOES.find(o => o.valor === status) || STATUS_OPCOES[0]).cor,
                    }}
                  />
                )}
              </span>
              <span className={classes.usuarioTextos}>
                <span className={classes.usuarioNome}>{user.name}</span>
                <span className={classes.usuarioPerfil}>{rotuloPerfil(user.profile)}</span>
              </span>
            </div>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
              PaperProps={{ className: classes.userMenuPaper }}
            >
              <div className={classes.menuHeader}>
                <Avatar
                  className={classes.menuHeaderAvatar}
                  src={user.profileImage ? `${getBackendUrl()}public/${user.profileImage}` : undefined}
                >
                  {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                </Avatar>
                <div>
                  <div className={classes.menuHeaderName}>{user.name}</div>
                  <div className={classes.menuHeaderRole}>
                    {rotuloPerfil(user.profile)}
                  </div>
                </div>
              </div>

              <div className={classes.menuSectionLabel}>Modo</div>
              <div className={classes.modeRow}>
                <div
                  className={clsx(classes.modeBtn, !darkMode && classes.modeBtnActive)}
                  onClick={() => { if (darkMode) toggleTheme(); }}
                >
                  <Brightness7Icon fontSize="small" /> Claro
                </div>
                <div
                  className={clsx(classes.modeBtn, darkMode && classes.modeBtnActive)}
                  onClick={() => { if (!darkMode) toggleTheme(); }}
                >
                  <Brightness4Icon fontSize="small" /> Escuro
                </div>
              </div>

              <Divider className={classes.menuDivider} />

              <MenuItem onClick={handleOpenUserModal} className={classes.userMenuItem}>
                <PersonOutlineIcon fontSize="small" className={classes.userMenuIcon} />
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              {poderDeAdmin(user.profile) && (
                <MenuItem
                  onClick={() => { history.push("/usuarios"); handleCloseMenu(); }}
                  className={classes.userMenuItem}
                >
                  <PeopleAltOutlinedIcon fontSize="small" className={classes.userMenuIcon} />
                  Usuários
                </MenuItem>
              )}

              <Divider className={classes.menuDivider} />

              <MenuItem
                onClick={handleClickLogout}
                className={clsx(classes.userMenuItem, classes.logoutItem)}
              >
                <ExitToAppIcon fontSize="small" className={classes.userMenuIcon} />
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </div>

        {mostraStatus && status !== "disponivel" && (
          <div
            className={classes.statusAviso}
            style={{
              backgroundColor: `${statusAtual.cor}1F`,
              borderColor: `${statusAtual.cor}66`,
            }}
          >
            <span
              className={clsx(classes.statusDot, classes.statusDotPulse)}
              style={{ backgroundColor: statusAtual.cor }}
            />
            <div>
              {autoAusente && status === "ausente" ? (
                <>Você ficou <b>20 minutos</b> sem mexer no sistema e foi colocado como{" "}
                <b style={{ color: statusAtual.cor }}>Ausente</b>. Nenhum cliente está sendo transferido para você.</>
              ) : (
                <>Você está como <b style={{ color: statusAtual.cor }}>{statusAtual.rotulo}</b>.
                {" "}Nenhum cliente está sendo transferido para você.</>
              )}
            </div>
            <button
              type="button"
              className={classes.statusAvisoBtn}
              onClick={() => handleChangeStatus("disponivel")}
            >
              Ficar Disponível
            </button>
          </div>
        )}

        {}
        <div className={classes.contentBody}>
          {children ? children : null}
        </div>
      </div>

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      {/* Calculadora e conversas minimizadas (flutuam por cima de tudo) */}
      <Flutuantes />
    </div>
  );
};

export default LoggedInLayout;
